import React from 'react';
import { Heart, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getIconByName } from '@/hooks/app-library/utils';
import { Progress } from '@/components/ui/progress';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';

interface CompactAppRequestCardProps {
  app: {
    id: string;
    name: string;
    description: string;
    category: string;
    icon_name: string;
    created_at?: string;
    favorite_count?: number;
  };
  isFavorited: boolean;
  onToggleFavorite: (appId: string) => void;
  onAppIdeaClick: (app: any) => void;
  isLoading?: boolean;
}

const CompactAppRequestCard: React.FC<CompactAppRequestCardProps> = ({
  app,
  isFavorited,
  onToggleFavorite,
  onAppIdeaClick,
  isLoading = false
}) => {
  const IconComponent = getIconByName(app.icon_name);
  
  // App Idea development progress calculations
  const DEVELOPMENT_THRESHOLD = 100;
  const favoriteCount = app.favorite_count || 0;
  const progressPercentage = Math.min((favoriteCount / DEVELOPMENT_THRESHOLD) * 100, 100);
  const likesToGo = Math.max(DEVELOPMENT_THRESHOLD - favoriteCount, 0);
  const isReadyForDevelopment = favoriteCount >= DEVELOPMENT_THRESHOLD;

  const handleCardClick = () => {
    onAppIdeaClick(app);
  };

  const handleToggleFavorite = async (appId: string) => {
    onToggleFavorite(appId);
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className="group relative bg-white/40 dark:bg-white/5 rounded-lg p-3 shadow-sm hover:shadow-md transition-all duration-200 border border-white/20 dark:border-white/10 cursor-pointer hover:bg-white/60 dark:hover:bg-white/10"
    >
      {/* Glass effect background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-white/5 dark:from-black/20 dark:to-black/10 backdrop-blur-sm -z-10 rounded-lg" />
      
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "p-2 rounded-lg flex-shrink-0",
          "bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900",
          "group-hover:from-brandBlue/10 group-hover:to-brandPurple/10"
        )}>
          <IconComponent 
            className="text-brandBlue dark:text-brandPurple" 
            size={20} 
          />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h3 className="font-semibold text-sm text-foreground truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {app.name}
          </h3>
          
          {/* Description */}
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
            {app.description}
          </p>
          
          {/* Progress Bar */}
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className={cn(
                "font-medium",
                isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
              )}>
                {isReadyForDevelopment ? "Ready! 🎉" : "Progress"}
              </span>
              <span className={cn(
                "font-semibold",
                isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
              )}>
                {favoriteCount}/{DEVELOPMENT_THRESHOLD}
              </span>
            </div>
            <Progress 
              value={progressPercentage} 
              className="h-1.5"
              indicatorClassName={cn(
                "transition-all duration-500",
                isReadyForDevelopment 
                  ? "bg-gradient-to-r from-green-500 to-emerald-500" 
                  : "bg-gradient-to-r from-orange-400 to-amber-500"
              )}
            />
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between mt-2">
            {/* Favorite button */}
            <PersistentFavoriteButton 
              itemId={app.id} 
              onToggleFavorite={handleToggleFavorite}
              isFavorite_prop={isFavorited}
              favoriteCount_prop={favoriteCount}
              className="p-0 scale-90"
            />
            
            {/* Category tag */}
            <div className={cn(
              "px-2 py-0.5 text-xs font-medium rounded-full",
              app.category === 'productivity' && "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
              app.category === 'creative' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
              app.category === 'education' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
              app.category === 'finance' && "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
              app.category === 'health' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
              app.category === 'entertainment' && "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
              app.category === 'shopping' && "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
            )}>
              {app.category.charAt(0).toUpperCase() + app.category.slice(1)}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CompactAppRequestCard; 